import { useQuery } from '@apollo/client/react';
import { faCalendarPlus, faImage } from '@fortawesome/free-regular-svg-icons';
import {
  faBook,
  faCalendarClock,
  faCircleExclamation,
  faCirclePlus,
  faFaceSmile,
  faMessageDots,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EmojiConvertor from 'emoji-js';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ChatQuery, CoachType, SelfDocument } from '../../graphql/types';
import useLocalStorage from '../../hooks/useLocalStorage';
import useTracking from '../../hooks/useTracking';
import { fileErrors } from '../../hooks/useUploadFile';
import { BLACK2, BORDER_GRAY, STATUS_ERROR } from '../../theme';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import { ChatEnterKeyBehaviour } from '../CoachSettings/Preferences/ChatSettings';
import { ContentLibraryModal } from '../ContentLibraryModal';
import { ButtonSize, ButtonVariant } from '../reusable/Button';
import { IconButton } from '../reusable/Button/IconButton';
import { LinkButton } from '../reusable/Button/LinkButton';
import Flyout, { FlyoutItem } from '../reusable/Flyout';
import Text, { TextColor, TextKind } from '../reusable/Text';
import Tooltip from '../reusable/Tooltip';
import { useChatContext } from './Context';
import { FilePreview } from './Image';
import { TemplateDefinition, TemplateMenu, TemplateOption } from './Templates';
import { useSendMessage } from './useSendMessage';
import { getScrollableDivInChat } from './util';

const emoji = new EmojiConvertor();

const StyledToolbar = styled.div`
  display: flex;
  padding: 8px;
  align-items: flex-end;
  gap: 4px;
`;

const TEXTAREA_MAX_HEIGHT = 336;
const StyledTextarea = styled.textarea<{
  $height: number;
}>`
  align-self: center;
  flex: 1;
  box-sizing: border-box;
  border: 1px solid ${BORDER_GRAY};
  border-radius: 16px;
  padding: 8px 16px;
  resize: none;
  width: 100%;
  height: ${({ $height }) => $height}px;
  min-height: 32px;
  max-height: ${TEXTAREA_MAX_HEIGHT}px;
  overflow: ${({ $height }) =>
    $height >= TEXTAREA_MAX_HEIGHT ? 'scroll' : 'hidden'};

  font-size: 12px;
  line-height: 16px;
  font-family: reader;
  font-weight: 400;
  color: ${BLACK2};
`;

const StyledError = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 16px 0;
  gap: 4px;
`;

export const Toolbar = ({
  chat,
  scrollContainerRef,
  onOpenBookSessionModal,
}: {
  chat?: ChatQuery['chatRoom'];
  scrollContainerRef?: React.Ref<HTMLDivElement>;
  onOpenBookSessionModal: () => void;
}) => {
  const { chatId, member } = useChatContext();
  const { track } = useTracking();

  const { data } = useQuery(SelfDocument);
  const [chatEnterKeyBehaviour] = useLocalStorage<ChatEnterKeyBehaviour>(
    `${data?.self?.id}-preferences-chat-enter-behaviour`,
    ChatEnterKeyBehaviour.Send,
  );

  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const actionMenuButtonRef = useRef(null);

  const [message, setMessage] = useState('');
  const { send, sendFile, validateFileInput, error } = useSendMessage();
  const [showTemplateItems, setShowTemplateItems] = useState(false);
  const [scrollHeight, setScrollHeight] = useState(32);
  const textInputRef = useRef<HTMLTextAreaElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileUploadInputRef = useRef<HTMLInputElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const [contentModalOpen, setContentModalOpen] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: eslint migration
  useEffect(() => {
    const textEl = textInputRef.current;
    if (textEl) {
      if (scrollHeight !== textEl.scrollHeight) {
        setScrollHeight(textEl.scrollHeight);
      }
    }
  }, [message, scrollHeight]);

  const handleChange = (e: React.FormEvent<HTMLTextAreaElement>) => {
    setMessage(emoji.replace_colons(e.currentTarget.value));
  };

  const appendMessage = (incomingString: string) => {
    if (message) {
      return setMessage(`${message}\n${incomingString}`);
    }
    return setMessage(incomingString);
  };

  useEffect(
    () => () => {
      setShowEmojiPicker(false);
    },
    [],
  );

  const handleSendMessage = async () => {
    const sendMessage = message;
    setMessage('');
    await send(sendMessage).catch(() => {
      setMessage(sendMessage);
    });

    const scrollableDiv = getScrollableDivInChat(
      scrollContainerRef as React.RefObject<HTMLDivElement>,
    );
    scrollableDiv?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (chatEnterKeyBehaviour === ChatEnterKeyBehaviour.NewLine) {
      if (
        e.key === 'Enter' &&
        (e.metaKey || e.ctrlKey) &&
        message.trim() !== ''
      ) {
        e.preventDefault();
        handleSendMessage();
        return;
      }
    }

    if (chatEnterKeyBehaviour === ChatEnterKeyBehaviour.Send) {
      if (e.key === 'Enter' && !e.shiftKey && message.trim() !== '') {
        e.preventDefault();
        handleSendMessage();
      }
    }
  };

  const handleSendSessionLink = () => {
    if (!member) {
      return;
    }

    track(EventNames.SchedulingIconClick, {
      event_category: EventCategories.Chat,
      chat_id: chatId,
    });

    const bookingType =
      data?.self?.coachData?.type === CoachType.Dietitian
        ? 'nutrition'
        : 'coaching';

    const link = `https://fit.teamexos.com/coachsessions/${bookingType}${`/${data?.self?.id ?? ''}`}`;

    appendMessage(link);
    textInputRef.current?.focus();
  };

  const trackActionMenuOpen = () => {
    track(EventNames.PlusIconClick, {
      event_category: EventCategories.Chat,
      chat_id: chatId,
    });
  };

  const handleUploadImageClick = (isRetry?: boolean, errorMessage?: string) => {
    if (isRetry) {
      track(EventNames.RetryClick, {
        event_category: EventCategories.Chat,
        event_label: errorMessage,
        event_subcategory: EventSubCategories.Image,
        chat_id: chatId,
      });
    } else {
      track(EventNames.MenuItemSelect, {
        event_category: EventCategories.Chat,
        event_label: 'Upload an image',
        event_subcategory: EventSubCategories.Image,
        chat_id: chatId,
      });
    }
    if (fileUploadInputRef.current?.value) {
      fileUploadInputRef.current.value = '';
    }
    fileUploadInputRef.current?.click();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    track(EventNames.FileSelection, {
      chat_id: chat?.id,
      event_category: EventCategories.Chat,
      event_label: e.target.files?.[0].type.split('/')[1],
    });
    setFile(validateFileInput(e));
  };

  const handleSendFile = async () => {
    if (!file) {
      return;
    }

    let failed = false;
    setUploadingFile(true);
    await sendFile(file)
      .catch(() => {
        failed = true;
      })
      .then(() => {
        if (!failed) {
          setFile(null);
        }
      })
      .finally(() => {
        setFile(null);
        setUploadingFile(false);
      });
  };
  const handleRemoveFile = () => {
    setFile(null);
    track(EventNames.RemoveAttachment, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Image,
      chat_id: chatId,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: when the chatId changes remove draft
  useEffect(() => {
    setFile(null);
    setMessage('');
  }, [chatId]);
  const handleFlyoutOpen = () => {
    setShowTemplateItems(false);
    trackActionMenuOpen();
  };

  const handleTemplateMenuOpen = () => {
    setShowTemplateItems(true);
    track(EventNames.MenuItemSelect, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Template,
      chat_id: chatId,
      event_label: 'Browse templates',
    });
  };

  const handleTemplateClick = (template: TemplateDefinition) => {
    setShowTemplateItems(false);
    setMessage(
      message !== '' ? `${message} ${template.message}` : template.message,
    );
  };

  const handleScheduleSessionClick = () => {
    track(EventNames.MenuItemSelect, {
      event_category: EventCategories.Chat,
      chat_id: chatId,
      event_label: 'Schedule a Session',
    });
    onOpenBookSessionModal();
  };

  const onContentModalClose = (url?: string) => {
    setContentModalOpen(false);
    if (url) {
      appendMessage(url);
    }
  };

  return (
    <>
      {error && (
        <StyledError>
          <FontAwesomeIcon icon={faCircleExclamation} color={STATUS_ERROR} />
          <Text color={TextColor.Error}>
            {error}{' '}
            {[fileErrors.TOO_BIG_GENERAL, fileErrors.TOO_BIG_IMAGE].includes(
              error,
            ) && (
              <LinkButton onClick={() => handleUploadImageClick(true, error)}>
                Retry
              </LinkButton>
            )}
          </Text>
        </StyledError>
      )}
      <StyledToolbar ref={toolbarRef}>
        <Flyout
          flyoutId="Header"
          menuProps={{
            anchorOrigin: { vertical: 'top', horizontal: 'left' },
            transformOrigin: { vertical: 'bottom', horizontal: 'left' },
          }}
          onOpen={handleFlyoutOpen}
          trigger={
            <IconButton
              ariaLabel="Open more actions"
              size={ButtonSize.Small}
              disabled={Boolean(file)}
              icon={
                <FontAwesomeIcon
                  ref={actionMenuButtonRef}
                  fontSize={20}
                  icon={faCirclePlus}
                />
              }
            />
          }
          tooltipContent="Open more actions"
        >
          <FlyoutItem
            onClick={handleTemplateMenuOpen}
            textKind={TextKind.LabelL}
            startIcon={<FontAwesomeIcon icon={faMessageDots} color={BLACK2} />}
          >
            Browse templates
          </FlyoutItem>
          <FlyoutItem
            onClick={() => handleUploadImageClick()}
            textKind={TextKind.LabelL}
            disabled={Boolean(message)}
            startIcon={<FontAwesomeIcon icon={faImage} color={BLACK2} />}
          >
            Upload a file
          </FlyoutItem>

          <FlyoutItem
            onClick={handleScheduleSessionClick}
            textKind={TextKind.LabelL}
            startIcon={<FontAwesomeIcon icon={faCalendarPlus} color={BLACK2} />}
          >
            Add a session
          </FlyoutItem>

          <FlyoutItem
            onClick={() => setContentModalOpen(true)}
            textKind={TextKind.LabelL}
            startIcon={<FontAwesomeIcon icon={faBook} color={BLACK2} />}
            trackingData={{
              event_category: EventCategories.Chat,
              event_label: EventLabels.OpenContentLibrary,
              chat_id: chatId,
            }}
          >
            Content Library
          </FlyoutItem>
        </Flyout>
        <TemplateMenu
          memberId={member?.id}
          chatId={chatId}
          fitClient={member?.fitClient}
          open={showTemplateItems}
          onBlur={() => setShowTemplateItems(false)}
          onTemplateClick={handleTemplateClick}
          templateOptions={{
            [TemplateOption.MEMBER_FIRST_NAME]: member?.firstName,
            [TemplateOption.COACH_FIRST_NAME]: data?.self?.firstName,
          }}
          anchorEl={actionMenuButtonRef.current} // Need this so the template menu also opens relative to the flyout trigger
        />
        <Tooltip
          disabled={Boolean(file)}
          content={
            <Text kind={TextKind.BodyS} color={TextColor.Contrast}>
              Send link to schedule a session
            </Text>
          }
        >
          <IconButton
            size={ButtonSize.Small}
            icon={<FontAwesomeIcon fontSize={20} icon={faCalendarClock} />}
            ariaLabel="Send link to schedule a session"
            onClick={handleSendSessionLink}
            disabled={Boolean(file)}
          />
        </Tooltip>
        {file ? (
          <FilePreview
            file={file}
            loading={uploadingFile}
            onRemoveFile={handleRemoveFile}
          />
        ) : (
          <StyledTextarea
            placeholder="Write message"
            value={message}
            onChange={handleChange}
            $height={scrollHeight}
            ref={textInputRef}
            aria-label="Write message"
            onKeyDown={handleKeyDown}
          />
        )}

        <IconButton
          variant={ButtonVariant.Transparent}
          size={ButtonSize.Small}
          icon={
            <FontAwesomeIcon
              fontSize={20}
              icon={showEmojiPicker ? faXmark : faFaceSmile}
            />
          }
          ariaLabel={
            showEmojiPicker ? 'Close Emoji Picker' : 'Show Emoji Picker'
          }
          onClick={() => {
            setShowEmojiPicker(!showEmojiPicker);
            const eventName = showEmojiPicker
              ? EventNames.CloseClick
              : EventNames.EmojiIconClick;
            track(eventName, {
              event_category: EventCategories.Chat,
              event_subcategory: EventSubCategories.Emojis,
              chat_id: chatId,
            });
          }}
        />

        <IconButton
          variant={ButtonVariant.Transparent}
          size={ButtonSize.Small}
          icon={<FontAwesomeIcon fontSize={20} icon={faPaperPlaneTop} />}
          disabled={(message.trim() === '' && !file) || uploadingFile}
          ariaLabel="Send message"
          onClick={file ? handleSendFile : handleSendMessage}
        />
      </StyledToolbar>
      <input
        data-testid="chat-image-upload"
        ref={fileUploadInputRef}
        type="file"
        accept={[
          'image/jpeg',
          'image/jpg',
          'image/png',
          'application/pdf',
        ].join(',')}
        multiple={false}
        style={{
          display: 'none',
        }}
        onChange={handleFileUpload}
      />
      <ContentLibraryModal
        isOpen={contentModalOpen}
        onClose={onContentModalClose}
      />
      {showEmojiPicker && (
        <div
          style={{ position: 'absolute', bottom: 42, right: 16, zIndex: 3 }}
          data-testid="emoji-picker"
        >
          <EmojiPicker
            theme={Theme.LIGHT}
            onEmojiClick={(event) => {
              track(EventNames.MenuItemSelect, {
                event_category: EventCategories.Chat,
                event_subcategory: EventSubCategories.Emojis,
                chat_id: chatId,
                event_label: event.names.join(','),
              });
              setMessage(message + event.emoji);
              setShowEmojiPicker(false);
            }}
          />
        </div>
      )}
    </>
  );
};
